/**
 * UI- and agent-ready projection of a node's [LocalSocialView].
 *
 * Where [LocalSocialView] exposes model types ([com.oasisfeng.invaluable.dsl.Post], commitments, observations), this
 * board flattens them into plain data the Web (Compose HTML) and the TUI/agent layers can render without re-walking
 * Link topology. Two invariants carry the product's soul:
 *
 * - **Missing observations are first-class.** A head whose body was withheld becomes a [MissingNotice] (issuer known,
 *   body unseen) — never a synthesized placeholder post.
 * - **References are content-addressed.** A [SocialRef] is a short prefix of a Link's projection, so the same post,
 *   actor, or route keeps the same handle across view rebuilds — letting a human click it or an agent name it in a
 *   text action, and letting [ActionGrammar] resolve it back to the model object.
 */

package com.oasisfeng.invaluable.social

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.ActorRoute
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.dsl.Post
import com.oasisfeng.invaluable.dsl.ProfileEvidence
import com.oasisfeng.invaluable.dsl.profile
import com.oasisfeng.invaluable.identity.Codec
import com.oasisfeng.invaluable.meaning.Utf8
import kotlin.math.ln

/** Stable, content-addressed short handle for a Link, shared by humans and agents to reference it across rebuilds. */
data class SocialRef(val value: String) {
    override fun toString(): String = value
}

/**
 * Compute the same short content-addressed [SocialRef] [SocialBoard] prints, for a link that is not (yet) in a board
 * snapshot — e.g. an object an action just authored, so its result can name it without a follow-up board rebuild.
 */
internal fun socialRefOf(link: NestedLink, projector: LinkProjector): SocialRef =
    SocialRef(Codec.base64Url(projector.project(link).payload.toByteArray()).take(SocialBoard.REF_LEN))

/** The same handle for a link already projected — used to name a private post by its head projection ([SocialNode]). */
internal fun socialRefOf(projection: LinkProjection): SocialRef =
    SocialRef(Codec.base64Url(projection.payload.toByteArray()).take(SocialBoard.REF_LEN))

/** A known actor with a short fingerprint handle and an optional locally selected profile appearance. */
data class ActorTag(val ref: SocialRef, val isMe: Boolean, val displayName: String? = null) {
    /** Human-facing label that keeps the content-addressed handle visible for disambiguation and actions. */
    val label: String get() = buildString {
        if (displayName == null) append(ref.value) else append(displayName).append(" [").append(ref.value).append(']')
        if (isMe) append(" (me)")
    }
}

/** One actor's conserved ownership fraction of a visible post, projected for compact UI breakdowns. */
data class ActorShare(val actor: ActorTag, val share: Double)

/**
 * A visible ActorRoute commitment that routes a post toward a recipient's attention — and the sender's own asset.
 *
 * The route carries its own valuation, separate from the post it points at: `recommend` mints an ownable, appreciating
 * claim, and an asset its owner cannot read is not a decision input at all. So "what happened to the attention I paid
 * for" is a reading here, never an inference from the subject post's numbers.
 */
data class PostRoute(
    val ref: SocialRef,
    val from: ActorTag,
    val to: ActorTag,
    /** The post whose attention this route carries; valued separately from the route itself. */
    val subjectRef: SocialRef,
    val cost: Quantity,
    /** Net value of this route in the local actor's own currency; null when no measuring frame exists. */
    val value: Double?,
    /** The local actor's conserved (frame-free) ownership fraction of this route's value; 0 when none. */
    val yourShare: Double,
    /** Whether the recipient endorsed this route or replied through it — the engagement a route appreciates on. */
    val answered: Boolean,
)

/** A visible post assertion: who authored it, its text, how it is backed, its valuation, and any visible replies. */
data class PostCard(
    val ref: SocialRef,
    val author: ActorTag,
    val text: String?,
    /** Quantity of the authoring commitment. */
    val cost: Quantity,
    /** Sum of every commitment targeting this post in the local snapshot. */
    val backingTotal: Quantity,
    /** Net value of this post's subtree in the local actor's own currency; null when no measuring frame exists. */
    val value: Double?,
    /** The local actor's conserved (frame-free) ownership fraction of this post's value; 0 when none. */
    val yourShare: Double,
    /** Non-zero conserved ownership fractions visible for this post, local actor first and then largest share first. */
    val actorShares: List<ActorShare>,
    /** Visible ActorRoute commitments whose subject is this post, in snapshot order. */
    val routes: List<PostRoute>,
    val replies: List<PostCard>,
)

/** A route addressed to the local actor (a recommendation), and the post it points at when that post is visible. */
data class InboxItem(
    val ref: SocialRef,
    val from: ActorTag,
    val subject: PostCard?,
    val subjectRef: SocialRef,
    val cost: Quantity,
    /** Whether the local actor has already responded to this route — endorsed it, or replied through it. Routes are
     * commitment events and never removed from the underlying snapshot, so this is the only signal that distinguishes
     * a route still awaiting a response from one already handled (app-social/README.md, "Inbox and change notification"). */
    val acted: Boolean,
)

/**
 * A head accepted but not fully visible: issuer known, body withheld — a missing observation, never a placeholder.
 *
 * [sealed] distinguishes the two dark forms (Milestone 13): when true, this node custodies the content as a sealed
 * ciphertext (present-but-locked — it could be unlocked by a disclosure) rather than the body being merely omitted.
 */
data class MissingNotice(val issuer: ActorTag?, val sealed: Boolean = false)

/**
 * A proposed push (roadmap Milestone 8): route any visible post to [to], an actor whose visible commitments make them a
 * likely backer. Acting on it is a plain `recommend`; the route is the pusher's own appreciating asset, so the
 * suggestion is an investment cue, not an automatic action.
 *
 * The subject need not be one the local actor already holds — paying for a route buys a share of what it routes, so an
 * unheld subject is a position to open, not an ineligible one. Read the local share off [BoardValuation.shareOf] to
 * tell the two apart.
 */
data class RouteSuggestion(
    val subjectRef: SocialRef,
    val subjectText: String?,
    val to: ActorTag,
    val reason: String,
    /** A default unit stake; the actor chooses the actual amount when recommending. */
    val suggestedCost: Quantity,
)

/**
 * The complete local-world snapshot a front-end renders.
 *
 * [feed] holds top-level posts (direct children of an actor's meaning origin) with their replies nested; comments and
 * route-replies are threaded under the post they answer. Resolution maps let [post]/[actor]/[route] turn a [SocialRef]
 * back into a model object so actions authored by humans or agents target the right Link.
 */
class SocialBoard internal constructor(
    val me: ActorTag,
    val feed: List<PostCard>,
    val inbox: List<InboxItem>,
    /** Every visible route in snapshot order, incoming and outgoing alike — the local actor's attention holdings. */
    val routes: List<PostRoute>,
    val missing: List<MissingNotice>,
    val actors: List<ActorTag>,
    /**
     * Proposed pushes (Milestone 8) across every visible post, strongest branch first. Complete and uncapped: renderers
     * decide how many to show, so that scoping to one post never loses its candidates to a global cut.
     */
    val suggestedRoutes: List<RouteSuggestion>,
    /** Economic reading of this board (Milestone 6), keyed by the same [SocialRef]s used everywhere else. */
    val valuation: BoardValuation,
    private val postsByRef: Map<String, Post>,
    private val actorsByRef: Map<String, Actor>,
    private val routesByRef: Map<String, ActorRoute>,
) {
    fun post(ref: SocialRef): Post? = postsByRef[ref.value]
    fun actor(ref: SocialRef): Actor? = actorsByRef[ref.value]
    fun route(ref: SocialRef): ActorRoute? = routesByRef[ref.value]

    /** Find a visible [PostCard] by [ref], searching the feed and its threaded replies; null if not visible. */
    fun card(ref: SocialRef): PostCard? {
        fun search(cards: List<PostCard>): PostCard? {
            for (card in cards) {
                if (card.ref == ref) return card
                search(card.replies)?.let { return it }
            }
            return null
        }
        return search(feed)
    }

    companion object {
        /** Short content-addressed reference length (base64url chars). Ample for the MVP's <10-node internal test. */
        internal const val REF_LEN = 10

        /** Assemble a board from a node's local view under [projector]'s projection scheme. */
        fun from(view: LocalSocialView, projector: LinkProjector): SocialBoard {
            val postsByRef = mutableMapOf<String, Post>()
            val actorsByRef = mutableMapOf<String, Actor>()
            val routesByRef = mutableMapOf<String, ActorRoute>()

            // Solve the value field once; every card and actor reads from this single solve (it is a fixed point).
            val reading = FieldReading.from(view)

            fun refOf(link: NestedLink) = socialRefOf(link, projector)
            fun displayNameOf(actor: Actor): String? = view.profile(actor)
                .mapNotNull { evidence -> Utf8.render(evidence.post.content())?.let { evidence to it } }
                .sortedWith(
                    compareByDescending<Pair<ProfileEvidence, String>> { it.first.backing }
                        .thenByDescending { it.first.selfAttested }
                        .thenBy { refOf(it.first.post.link).value },
                )
                .firstOrNull()?.second
            fun tagOf(actor: Actor): ActorTag {
                val ref = refOf(actor.link)
                actorsByRef[ref.value] = actor
                return ActorTag(ref, isMe = actor == view.localActor, displayName = displayNameOf(actor))
            }

            // One authoring commitment per visible post (first wins, deterministic in snapshot order).
            val authored = LinkedHashMap<NestedLink, Pair<Post, Commitment>>()
            val profiledActors = view.snapshot.commitments.mapNotNullTo(linkedSetOf()) { commitment ->
                Post.render(commitment.targetLink)?.profile()?.actor()
            }
            for (vc in view.rendering.visible) {
                when (val target = vc.target) {
                    // A profile label (display name, etc.) lives under the actor -> MEANING -> actor anchor; it surfaces
                    // as the actor's displayName and under "Known actors", not as content — so keep it out of the Feed
                    // (otherwise every actor injects a name-only post that reads as indistinguishable noise; S5 finding).
                    is Post -> if (target.profile() == null) authored.getOrPut(target.link) { target to vc.commitment }
                    is ActorRoute -> routesByRef.getOrPut(refOf(target.link).value) { target }
                }
            }
            for ((link, pair) in authored) postsByRef[refOf(link).value] = pair.first

            // Thread each post under the visible post it answers; orphans and shares stay top-level.
            fun parentOf(post: Post): NestedLink? {
                val cause = post.cause()
                if (Actor.render(cause) != null) return null
                if (authored.containsKey(cause)) return cause
                val viaRoute = ActorRoute.render(cause)?.subject
                return viaRoute?.takeIf(authored::containsKey)
            }
            val parents = authored.keys.associateWith { parentOf(authored.getValue(it).first) }
            val childrenOf = authored.keys.filter { parents[it] != null }.groupBy { parents.getValue(it)!! }
            // A route is a commitment event: replying or endorsing it adds a *new* commitment, it never removes the
            // route itself from the snapshot. So "has this actor acted on it" has to be computed here, once, from the
            // same signals AgentTools.inboxDigest() used to only check itself — see app-social/README.md, "Inbox and
            // change notification". Asked of the recipient it reads as "did my recommendation land"; asked of the local
            // actor it is the Inbox's pending/handled split.
            fun actedOn(route: ActorRoute, by: Actor): Boolean {
                val endorsed = view.backers(route).any { it.issuer == by }
                val replied = view.rendering.visible.any { vc ->
                    (vc.target as? Post)?.cause() == route.link && vc.commitment.issuer == by
                }
                return endorsed || replied
            }
            // One opening commitment per visible route — the same first-wins rule authored posts follow above. A route
            // is a Link, so every *later* commitment on it (an endorse, a plain back) renders as that same ActorRoute:
            // projecting per visible commitment listed one route once per commitment, each labelled with that
            // committer's identity and stake as if they had opened it (so an endorse read as "the endorser routed this
            // post to themselves"). Whoever opened the route staked the first commitment on it.
            val opened = LinkedHashMap<NestedLink, Pair<ActorRoute, Commitment>>()
            for (vc in view.rendering.visible) {
                val route = vc.target as? ActorRoute ?: continue
                opened.getOrPut(route.link) { route to vc.commitment }
            }
            val routesBySubject = LinkedHashMap<NestedLink, MutableList<PostRoute>>()
            val routes = mutableListOf<PostRoute>()
            for ((link, opening) in opened) {
                val (route, commitment) = opening
                routesByRef[refOf(link).value] = route
                val projected = PostRoute(
                    ref = refOf(link),
                    from = tagOf(commitment.issuer),
                    to = tagOf(route.recipient),
                    subjectRef = refOf(route.subject),
                    cost = commitment.quantity,
                    value = reading.value(link),
                    yourShare = reading.share(link),
                    answered = actedOn(route, route.recipient),
                )
                routes.add(projected)
                routesBySubject.getOrPut(route.subject) { mutableListOf() }.add(projected)
            }

            fun card(link: NestedLink): PostCard {
                val (post, commitment) = authored.getValue(link)
                val backingTotal = view.backers(post).fold(Quantity.ZERO) { sum, c -> sum + c.quantity }
                return PostCard(
                    ref = refOf(link),
                    author = tagOf(commitment.issuer),
                    text = post.textOrNull(),
                    cost = commitment.quantity,
                    backingTotal = backingTotal,
                    value = reading.value(link),
                    yourShare = reading.share(link),
                    actorShares = reading.shares(link).map { (actor, share) -> ActorShare(tagOf(actor), share) },
                    routes = routesBySubject[link].orEmpty(),
                    replies = childrenOf[link].orEmpty().map(::card),
                )
            }
            val feed = authored.keys.filter { parents[it] == null }.map(::card)

            // An Inbox entry is a route, not each commitment that lands on one: endorsing a route addressed to you adds
            // a commitment to it, which without this would re-list the same route as if a second sender (you) had
            // routed the post to yourself, with your endorsement mistaken for their stake.
            val inbox = view.inbox().distinctBy { it.target.link }.map { vc ->
                val route = vc.target
                routesByRef[refOf(route.link).value] = route
                val subjectRef = refOf(route.subject)
                InboxItem(
                    ref = refOf(route.link),
                    from = tagOf(vc.commitment.issuer),
                    subject = postsByRef[subjectRef.value]?.let { card(it.link) },
                    subjectRef = subjectRef,
                    cost = vc.commitment.quantity,
                    acted = actedOn(route, view.localActor),
                )
            }

            val missing = view.missing.map { observed ->
                MissingNotice(
                    issuer = (observed.issuer as? Observation.Known)?.value?.let(::tagOf),
                    sealed = observed.contentProjection?.let { it in view.sealedContentProjections } ?: false,
                )
            }

            // Every actor we know of becomes a recommend target: the local actor, every issuer, every route recipient.
            val knownActors = LinkedHashMap<NestedLink, ActorTag>()
            knownActors[view.localActor.link] = tagOf(view.localActor)
            for (issuer in view.snapshot.issuers()) knownActors.getOrPut(issuer.link) { tagOf(issuer) }
            for (route in routesByRef.values) knownActors.getOrPut(route.recipient.link) { tagOf(route.recipient) }
            for (actor in profiledActors) knownActors.getOrPut(actor.link) { tagOf(actor) }

            // Push suggestions span **every** visible post, not only the ones the local actor already stakes. Paying
            // for a route is itself a claim on what it routes — an opener takes a share even of a subject they never
            // staked in (~19.5% in RouteReturnTest) — so the old "must already hold it" filter hid exactly the case
            // where a route both opens a position and directs attention, from the one tool meant to surface it.
            //
            // The branch (subject + route) is the unit of account, so a candidate is scored on the whole branch: the
            // recipient's pull from [AudienceValuation] times the subject's own weight, the latter damped so it orders
            // without drowning proximity. Held and unheld subjects compete on that single score with no thumb on the
            // scale — which of the two pays better follows from the numbers, and nothing measured says it is always
            // the same one. Recipients come from the same value frame as the rest of the board, so a richer likely
            // backer reads as a stronger pull.
            //
            // The list is complete and globally ranked; **capping is the renderer's job**. `get_context` must not grow
            // with the pool (dog-fooding.md#过门与止损e-系列), while a front-end asking about one post wants every
            // candidate for it — a cap applied here would silently answer "none" for a post that has candidates.
            val suggestedRoutes = run {
                val locallyCommittedRoutes = view.snapshot.commitments.mapNotNullTo(mutableSetOf()) { commitment ->
                    if (commitment.issuer != view.localActor) return@mapNotNullTo null
                    ActorRoute.render(commitment.targetLink)?.let { it.subject to it.recipient }
                }
                val scored = mutableListOf<Pair<Double, RouteSuggestion>>()
                for ((link, pair) in authored) {
                    val post = pair.first
                    // Frame-relative worth where a measuring frame exists, gross stake on the post where it does not —
                    // the same "relative if priced, frame-free otherwise" fallback AudienceValuation uses for capacity.
                    val weight = reading.value(link)
                        ?: view.snapshot.commitmentsOf(link).sumOf { it.quantity.abs().toDouble() }
                    val subjectPull = 1.0 + ln(1.0 + weight.coerceAtLeast(0.0))
                    for (candidate in AudienceValuation.rank(post, view.snapshot, view.localActor, priceOf = reading::price)) {
                        if ((link to candidate.recipient) in locallyCommittedRoutes) continue
                        scored += candidate.score * subjectPull to
                            RouteSuggestion(refOf(link), post.textOrNull(), tagOf(candidate.recipient), candidate.reason, Quantity.ONE)
                    }
                }
                scored.sortedWith(
                    compareByDescending<Pair<Double, RouteSuggestion>> { it.first }
                        .thenBy { it.second.subjectRef.value }
                        .thenBy { it.second.to.ref.value },
                ).map { it.second }
            }

            // Flatten the field reading onto the same refs the rest of the board uses, so front-ends look valuation up
            // by the handle they already hold. Routes are projected alongside posts: the engine has always valued them
            // (a route is an ownable, appreciating claim — the first value proposition rests on it), and walking only
            // authored posts here made that asset unreadable to the very actor who owns it.
            val linkValue = mutableMapOf<String, Double>()
            val linkShare = mutableMapOf<String, Double>()
            val linkResidual = mutableMapOf<String, Double>()
            for (link in authored.keys + routesByRef.values.map(ActorRoute::link)) {
                val ref = refOf(link).value
                reading.value(link)?.let { linkValue[ref] = it }
                linkShare[ref] = reading.share(link)
                if (reading.priced) linkResidual[ref] = reading.residual(link)
            }
            val actorPrice = mutableMapOf<String, Double>()
            val actorAssets = mutableMapOf<String, Double>()
            val actorIssuance = mutableMapOf<String, Double>()
            for ((ref, actor) in actorsByRef) {
                reading.price(actor)?.let { actorPrice[ref] = it }
                reading.assets(actor)?.let { actorAssets[ref] = it }
                actorIssuance[ref] = reading.issuance(actor)
            }
            val valuation = BoardValuation(
                priced = reading.priced,
                linkValue = linkValue,
                linkShare = linkShare,
                linkResidual = linkResidual,
                actorPrice = actorPrice,
                actorAssets = actorAssets,
                actorIssuance = actorIssuance,
            )

            return SocialBoard(
                me = tagOf(view.localActor),
                feed = feed,
                inbox = inbox,
                routes = routes,
                missing = missing,
                actors = knownActors.values.toList(),
                suggestedRoutes = suggestedRoutes,
                valuation = valuation,
                postsByRef = postsByRef,
                actorsByRef = actorsByRef,
                routesByRef = routesByRef,
            )
        }
    }
}
